/**
 * Pure detection/parse/strip helpers for the courses admin-preview entry flow.
 *
 * HMI (`membership-hmi-app` theme builder) opens the clientportal courses host with an
 * agency IAM Bearer in the URL, e.g.:
 *   /courses/products/{productId}/?token={agencyBearer}&location_id={loc}&company_id={company}&is_preview=true&builder=true&template_id={id}
 *
 * Framework-free so `@plugins/utils/magic-link`, the clientportal auth middleware, the
 * courses access guard, and the admin-preview middleware can share it without pulling in
 * Vue/Pinia/Nuxt. Scope is intentionally limited to TOP-LEVEL `/courses` paths — communities
 * learning paths (`/communities/.../learning/courses/...`) and other routes are untouched.
 */

import { parseQueryPreservingPlus, splitUrlOnce } from '@common/core-layer/utils/url-query-parse.utils'

export const COURSES_ADMIN_PREVIEW_ROUTE_PREFIX = '/courses' as const

export const ADMIN_PREVIEW_FLAG_QUERY_KEY = 'is_preview' as const
export const ADMIN_PREVIEW_TOKEN_QUERY_KEY = 'token' as const
export const ADMIN_PREVIEW_LOCATION_QUERY_KEY = 'location_id' as const
export const ADMIN_PREVIEW_BUILDER_QUERY_KEY = 'builder' as const
export const ADMIN_PREVIEW_TEMPLATE_QUERY_KEY = 'template_id' as const
export const ADMIN_PREVIEW_COMPANY_QUERY_KEY = 'company_id' as const

/** Query keys owned by an active host preview session. */
export const ADMIN_PREVIEW_IDENTITY_QUERY_KEYS = [
  ADMIN_PREVIEW_FLAG_QUERY_KEY,
  ADMIN_PREVIEW_LOCATION_QUERY_KEY,
  ADMIN_PREVIEW_BUILDER_QUERY_KEY,
  ADMIN_PREVIEW_COMPANY_QUERY_KEY,
] as const

/** Non-sensitive preview keys that may be carried across course navigation. */
export const ADMIN_PREVIEW_CONTEXT_QUERY_KEYS = [
  ...ADMIN_PREVIEW_IDENTITY_QUERY_KEYS,
  ADMIN_PREVIEW_TEMPLATE_QUERY_KEY,
] as const

export interface CoursesAdminPreviewCredential {
  /** Agency IAM Bearer exchanged by the federated remote via `POST /user/login-token`. */
  token: string
  locationId: string
  builder: boolean
  templateId: string | null
  /**
   * Agency company id from the builder preview URL (`company_id`). Forwarded to the v2
   * `/login-token` exchange so membership-backend can fall back to an agency-user lookup by
   * email + company when the CP contact has no mapped agency user id (mirrors
   * membership-hmi-preview PR #1329). `null` when the URL omits it.
   */
  companyId: string | null
}

export interface CoursesAdminPreviewContext {
  locationId: string
  builder: boolean
  templateId: string | null
  companyId?: string | null
}

// Compatible with vue-router's `LocationQuery` (array members may be null).
type RouteQueryValue = string | null | undefined | Array<string | null>
export type CoursesAdminPreviewRouteQuery = Record<string, RouteQueryValue>
export type CoursesAdminPreviewNavigationQuery = Record<string, string | number | boolean | string[]>

interface RawAdminPreviewParams {
  isPreview: string | null
  token: string | null
  locationId: string | null
  builder: string | null
  templateId: string | null
  companyId: string | null
}

/** Top-level `/courses` only — NOT communities/gokollab embedded learning paths. */
export function isCoursesAdminPreviewRoute(path: string): boolean {
  return path === COURSES_ADMIN_PREVIEW_ROUTE_PREFIX || path.startsWith(`${COURSES_ADMIN_PREVIEW_ROUTE_PREFIX}/`)
}

/** `true | 1 | yes` (case-insensitive) → true. Anything else → false. Mirrors the beta-preview flag parser. */
export function isAdminPreviewFlagEnabled(value: string | null | undefined): boolean {
  if (value == null) return false
  const normalized = value.toLowerCase()
  return normalized === 'true' || normalized === '1' || normalized === 'yes'
}

/** v2 preview token prefix. Client-portal magic-link tokens for authV2 locations are `v2.`-prefixed. */
export const V2_PREVIEW_TOKEN_PREFIX = 'v2.' as const

/**
 * Whether a token is a v2 (authV2Enabled) preview token, i.e. `v2.`-prefixed (mirrors
 * membership-hmi-preview `isV2PreviewToken`). Only selects the v2 vs legacy v1 exchange path —
 * NOT an "is admin preview" signal, since normal authV2 login tokens are also `v2.`-prefixed.
 * That gate is {@link isCoursesAdminPreviewCredential}.
 */
export function isV2PreviewToken(token: string | null | undefined): boolean {
  return typeof token === 'string' && token.startsWith(V2_PREVIEW_TOKEN_PREFIX)
}

/** Picks a single string out of `route.query`-style values (string | string[] | undefined). */
function pickFirst(value: RouteQueryValue): string | null {
  if (value == null) return null
  const firstEntry = Array.isArray(value) ? value[0] : value
  if (firstEntry === undefined || firstEntry === null) return null
  const stringValue = String(firstEntry)
  return stringValue === '' ? null : stringValue
}

/** A credential requires the preview flag enabled AND a non-empty token AND a non-empty location. */
function buildCredential(params: RawAdminPreviewParams): CoursesAdminPreviewCredential | null {
  if (!isAdminPreviewFlagEnabled(params.isPreview)) return null
  if (!params.token) return null
  if (!params.locationId) return null

  return {
    token: params.token,
    locationId: params.locationId,
    builder: isAdminPreviewFlagEnabled(params.builder),
    templateId: params.templateId,
    companyId: params.companyId,
  }
}

function paramsFromRecord(params: Record<string, string>): RawAdminPreviewParams {
  return {
    isPreview: params[ADMIN_PREVIEW_FLAG_QUERY_KEY] ?? null,
    token: params[ADMIN_PREVIEW_TOKEN_QUERY_KEY] ?? null,
    locationId: params[ADMIN_PREVIEW_LOCATION_QUERY_KEY] ?? null,
    builder: params[ADMIN_PREVIEW_BUILDER_QUERY_KEY] ?? null,
    templateId: params[ADMIN_PREVIEW_TEMPLATE_QUERY_KEY] ?? null,
    companyId: params[ADMIN_PREVIEW_COMPANY_QUERY_KEY] ?? null,
  }
}

/** Parse an admin-preview credential from a Vue Router-style query object. Returns `null` when absent/incomplete. */
export function parseCoursesAdminPreviewFromQuery(query: CoursesAdminPreviewRouteQuery): CoursesAdminPreviewCredential | null {
  return buildCredential({
    isPreview: pickFirst(query[ADMIN_PREVIEW_FLAG_QUERY_KEY]),
    token: pickFirst(query[ADMIN_PREVIEW_TOKEN_QUERY_KEY]),
    locationId: pickFirst(query[ADMIN_PREVIEW_LOCATION_QUERY_KEY]),
    builder: pickFirst(query[ADMIN_PREVIEW_BUILDER_QUERY_KEY]),
    templateId: pickFirst(query[ADMIN_PREVIEW_TEMPLATE_QUERY_KEY]),
    companyId: pickFirst(query[ADMIN_PREVIEW_COMPANY_QUERY_KEY]),
  })
}

/** True when the route + query is an admin-preview entry on a top-level `/courses` path. */
export function isCoursesAdminPreviewCredential(path: string, query: CoursesAdminPreviewRouteQuery): boolean {
  return isCoursesAdminPreviewRoute(path) && parseCoursesAdminPreviewFromQuery(query) != null
}

/**
 * Raw-string variant for the shared magic-link util, which operates on `pathname` + `queryString`
 * before Vue Router has parsed the URL. Uses `parseQueryPreservingPlus` so `+` in values is kept.
 */
export function isCoursesAdminPreviewCredentialFromParts(pathname: string, queryString: string): boolean {
  if (!isCoursesAdminPreviewRoute(pathname)) return false
  return buildCredential(paramsFromRecord(parseQueryPreservingPlus(queryString))) != null
}

/**
 * Parses the non-sensitive admin-preview *context* (no `token`) from a query. `InitThemeLayout`
 * strips `token` after first load but keeps `is_preview`/`location_id`/`builder`/`template_id`,
 * so this identifies a *reloaded* preview once the token is gone.
 */
export function parseCoursesAdminPreviewContextFromQuery(query: CoursesAdminPreviewRouteQuery): CoursesAdminPreviewContext | null {
  const locationId = pickFirst(query[ADMIN_PREVIEW_LOCATION_QUERY_KEY])
  if (!isAdminPreviewFlagEnabled(pickFirst(query[ADMIN_PREVIEW_FLAG_QUERY_KEY])) || !locationId) return null
  return {
    locationId,
    builder: isAdminPreviewFlagEnabled(pickFirst(query[ADMIN_PREVIEW_BUILDER_QUERY_KEY])),
    templateId: pickFirst(query[ADMIN_PREVIEW_TEMPLATE_QUERY_KEY]),
    companyId: pickFirst(query[ADMIN_PREVIEW_COMPANY_QUERY_KEY]),
  }
}

/**
 * True when the route + query looks like an admin-preview *context* even after `token` is
 * stripped (`/courses` + `is_preview` + `location_id`). Used ONLY for the SSR shell render on
 * reload, since the server can't read the client-only `acatv2` session and must defer the auth
 * decision to the client. Requires `is_preview` so a normal learner deep-link never matches.
 */
export function isCoursesAdminPreviewContext(path: string, query: CoursesAdminPreviewRouteQuery): boolean {
  return isCoursesAdminPreviewRoute(path) && parseCoursesAdminPreviewContextFromQuery(query) != null
}

/**
 * Canonicalize a target `/courses` query using active host preview context.
 *
 * Preview identity always comes from the active host session. Route-specific values and an
 * existing `template_id` survive, while `token` and stale optional identity are removed.
 */
export function mergeCoursesAdminPreviewQuery(
  query: CoursesAdminPreviewNavigationQuery,
  context: CoursesAdminPreviewContext
): CoursesAdminPreviewNavigationQuery
export function mergeCoursesAdminPreviewQuery(
  query: CoursesAdminPreviewRouteQuery,
  context: CoursesAdminPreviewContext
): CoursesAdminPreviewRouteQuery
export function mergeCoursesAdminPreviewQuery(
  query: CoursesAdminPreviewNavigationQuery | CoursesAdminPreviewRouteQuery,
  context: CoursesAdminPreviewContext
): CoursesAdminPreviewNavigationQuery | CoursesAdminPreviewRouteQuery {
  const merged = { ...query }
  delete merged[ADMIN_PREVIEW_TOKEN_QUERY_KEY]

  merged[ADMIN_PREVIEW_FLAG_QUERY_KEY] = 'true'
  merged[ADMIN_PREVIEW_LOCATION_QUERY_KEY] = context.locationId

  if (context.builder) {
    merged[ADMIN_PREVIEW_BUILDER_QUERY_KEY] = 'true'
  } else {
    delete merged[ADMIN_PREVIEW_BUILDER_QUERY_KEY]
  }
  if (context.templateId && !pickFirst(merged[ADMIN_PREVIEW_TEMPLATE_QUERY_KEY])) {
    merged[ADMIN_PREVIEW_TEMPLATE_QUERY_KEY] = context.templateId
  }
  if (context.companyId) {
    merged[ADMIN_PREVIEW_COMPANY_QUERY_KEY] = context.companyId
  } else {
    delete merged[ADMIN_PREVIEW_COMPANY_QUERY_KEY]
  }

  return merged
}

/**
 * Returns the URL with the agency Bearer (`token`) removed, preserving every other param.
 * Used to scrub the address bar after the in-memory store has captured the token.
 */
export function stripAdminPreviewTokenFromUrl(rawUrl: string): string {
  const [path, queryString] = splitUrlOnce(rawUrl)
  const params = parseQueryPreservingPlus(queryString)
  if (params[ADMIN_PREVIEW_TOKEN_QUERY_KEY] === undefined) return rawUrl

  delete params[ADMIN_PREVIEW_TOKEN_QUERY_KEY]

  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, value)
    }
  }
  const nextQuery = search.toString()
  return nextQuery ? `${path}?${nextQuery}` : path
}
